import { useEffect, useState, useMemo, useCallback } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  MenuItem, TextField, InputAdornment, Avatar, Chip, Stack,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from "@mui/material";
import DataGrid from './ui/DataTable';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';

import axiosInstance from './axiosInstance';
import { isValidEmail } from "../utils/validation";
import { useSnackbar } from "../providers/SnackbarProvider";
import { useBreadcrumbs } from './BreadcrumbBar';
import SectionCard from './ui/SectionCard';
import EmptyState from './ui/EmptyState';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import { phaseChipColors } from '../utils/phaseColors';
import {
  accountTypeOptions, accountSegmentOptions,
  organizationTypeOptions, organizationSizeOptions, revenueRangeOptions,
  industryOptions, legalFormOptions,
} from '../constants/accountOptions';

const labelMap = (opts) => Object.fromEntries(opts.map((o) => [o.value, o.label]));
const TYPE_LABELS = labelMap(accountTypeOptions);
const INDUSTRY_LABELS = labelMap(industryOptions);

const initials = (name) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return isNaN(days) ? null : Math.max(0, days);
};

// Leerzustand bei Filter/Suche ohne Treffer (D4) — Slots erwarten Komponenten
const NoResults = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);

const EMPTY_ACCOUNT = {
  name: '', phone: '', email: '', street: '', city: '', zip_code: '',
  website: '', status: 'Open', account_type: '',
};

function AccountList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  // Pipeline-Konfiguration (B3): Phasen/Labels + Stale-Schwellen aus dem Admin
  const { phasesFor, lostFor, statusLabelsFor, staleHintDays, staleWarnDays } = usePipelineConfig();
  const accountPhases = phasesFor('account');
  const accountLost = lostFor('account');
  const STATUS_LABELS = statusLabelsFor('account');

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newAccount, setNewAccount] = useState(EMPTY_ACCOUNT);

  // Filter
  const [search, setSearch] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('ALL');
  const [fType, setFType] = useState('');
  const [fIndustry, setFIndustry] = useState('');

  useBreadcrumbs([{ label: 'Unternehmen' }]);

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/accounts/');
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setOpen(false); setNewAccount(EMPTY_ACCOUNT); };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (newAccount.email !== '' && !isValidEmail(newAccount.email)) {
      showSnackbar("Ungültige E-Mail-Adresse", "error");
      return;
    }
    try {
      await axiosInstance.post('/accounts/', newAccount);
      fetchAccounts();
      handleClose();
      showSnackbar('Unternehmen erfolgreich hinzugefügt', "success");
    } catch (error) {
      showSnackbar('Fehler beim Erstellen des Unternehmens', "error");
      console.error('Error creating account:', error);
    }
  };

  // Soft-Chip-Stil aus semantischen Farben
  const chipSx = (color) => ({ bgcolor: alpha(color, 0.13), color, fontWeight: 600 });
  // Design 3.0: Phasen-Chips aus der Akzent-Ramp (letzte Phase grün, Verlust rot)
  const chipColors = phaseChipColors(theme, accountPhases, accountLost);
  const statusColor = (s) => chipColors[s] || theme.palette.text.secondary;

  const dash = (v) => (v && String(v).trim() ? v : '—');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (phaseFilter !== 'ALL' && a.status !== phaseFilter) return false;
      if (fType && a.account_type !== fType) return false;
      if (fIndustry && a.industry !== fIndustry) return false;
      if (!q) return true;
      const contact = a.contact_person_details
        ? `${a.contact_person_details.first_name || ''} ${a.contact_person_details.last_name || ''}` : '';
      return [a.name, a.email, a.city, contact].some((f) => (f || '').toLowerCase().includes(q));
    });
  }, [accounts, search, phaseFilter, fType, fIndustry]);

  // ---------- Leerzustände (D4) ----------
  // Suche/Filter laufen außerhalb der Grid — bei aktivem Filter ohne Treffer
  // sieht die Grid daher ebenfalls „keine Zeilen". NoRows unterscheidet
  // deshalb selbst zwischen echter Leere und leergefiltertem Bestand.
  const NoRows = useCallback(() => (
    accounts.length ? <NoResults /> : (
      <EmptyState
        title="Noch keine Unternehmen."
        hint="Leg das erste an oder konvertiere einen Lead."
      />
    )
  ), [accounts.length]);

  // „In Phase seit" — für laufende Phasen; färbt ab den Stale-Schwellen
  const renderInPhaseCell = (params) => {
    const { row } = params;
    if (row.status === accountPhases[accountPhases.length - 1]?.key || row.status === accountLost?.key) {
      return <Typography variant="caption" color="text.disabled">—</Typography>;
    }
    const days = daysSince(row.phase_changed_at);
    if (days === null) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const color = days >= staleWarnDays ? 'error.main' : days >= staleHintDays ? 'warning.main' : 'text.secondary';
    const weight = days >= staleHintDays ? 700 : 400;
    return (
      <Typography variant="body2" sx={{ color, fontWeight: weight, fontVariantNumeric: 'tabular-nums' }}>
        {days === 0 ? 'heute' : `${days} ${days === 1 ? 'Tag' : 'Tage'}`}
      </Typography>
    );
  };

  const renderChecklistCell = (params) => {
    const phase = accountPhases.find((p) => p.key === params.row.status);
    if (!phase || !phase.checklist.length) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const done = ((params.row.checklist_state && params.row.checklist_state[phase.key]) || []).length;
    const total = phase.checklist.length;
    const complete = done >= total;
    return (
      <Typography variant="body2" sx={{
        color: complete ? 'success.main' : 'text.secondary',
        fontWeight: complete ? 700 : 400, fontVariantNumeric: 'tabular-nums',
      }}>
        {done}/{total}
      </Typography>
    );
  };

  const columns = [
    {
      field: 'name', headerName: 'Unternehmen', flex: 1.4, minWidth: 220,
      renderCell: (params) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', color: 'primary.dark', fontSize: '0.8rem', fontWeight: 700, flex: 'none' }}>
            {initials(params.row.name)}
          </Avatar>
          <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{params.row.name}</Typography>
            {params.row.city && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{params.row.city}</Typography>}
          </Box>
        </Stack>
      ),
    },
    {
      field: 'status', headerName: 'Phase', width: 150,
      renderCell: (params) => params.row.status
        ? <Chip size="small" label={STATUS_LABELS[params.row.status] || params.row.status} sx={chipSx(statusColor(params.row.status))} />
        : <Box sx={{ color: 'text.disabled' }}>—</Box>,
    },
    {
      field: 'phase_changed_at', headerName: 'In Phase seit', width: 115,
      valueGetter: (value) => daysSince(value) ?? -1,
      renderCell: renderInPhaseCell,
    },
    {
      field: 'checklist', headerName: 'Checkliste', width: 95, sortable: false,
      renderCell: renderChecklistCell,
    },
    {
      field: 'account_type', headerName: 'Typ', width: 120,
      renderCell: (params) => params.row.account_type
        ? <Chip size="small" label={TYPE_LABELS[params.row.account_type] || params.row.account_type}
            sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 600 }} />
        : <Box sx={{ color: 'text.disabled' }}>—</Box>,
    },
    {
      field: 'industry', headerName: 'Branche', flex: 1, minWidth: 140,
      renderCell: (params) => <Typography variant="body2" color="text.secondary" noWrap>{dash(INDUSTRY_LABELS[params.row.industry] || params.row.industry)}</Typography>,
    },
    {
      field: 'contact_person', headerName: 'Primärer Kontakt', flex: 1, minWidth: 150,
      renderCell: (params) => {
        const c = params.row?.contact_person_details;
        const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '';
        return <Typography variant="body2" sx={{ color: name ? 'text.primary' : 'text.disabled' }} noWrap>{name || '—'}</Typography>;
      },
    },
    {
      field: 'email', headerName: 'E-Mail', flex: 1, minWidth: 160,
      renderCell: (p) => <Typography variant="body2" color="text.secondary" noWrap>{dash(p.row.email)}</Typography>,
    },
  ];

  const filtersActive = search || phaseFilter !== 'ALL' || fType || fIndustry;
  const phaseFilters = [{ key: 'ALL', label: 'Alle' }, ...accountPhases.map((p) => ({ key: p.key, label: p.label })), ...(accountLost ? [{ key: accountLost.key, label: accountLost.label }] : [])];

  return (
    <Box>
      {/* Kopfzeile */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">Unternehmen</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {filtered.length} {filtered.length === 1 ? 'Eintrag' : 'Einträge'}{filtersActive ? ` (von ${accounts.length})` : ''}
          </Typography>
        </Box>
        <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }}>
          Neues Unternehmen
        </Button>
      </Stack>

      {/* Suche + stille Phasen-Filter + Selects */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small" placeholder="Suchen …" value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240, maxWidth: 360, bgcolor: 'background.paper' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment> }}
        />
        <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
          {phaseFilters.map((f) => {
            const active = phaseFilter === f.key;
            return (
              <Box
                key={f.key} role="button" tabIndex={0}
                onClick={() => setPhaseFilter(f.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPhaseFilter(f.key); } }}
                sx={{
                  fontSize: '0.8rem', fontWeight: 600, px: active ? 1.25 : 0.75, py: 0.3, borderRadius: 999,
                  cursor: 'pointer', userSelect: 'none',
                  color: active ? 'primary.main' : 'text.disabled',
                  bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': { color: active ? 'primary.main' : 'text.secondary' },
                  '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                }}
              >
                {f.label}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField select size="small" label="Typ" value={fType} onChange={(e) => setFType(e.target.value)} sx={{ minWidth: 140, bgcolor: 'background.paper' }}>
          <MenuItem value="">Alle</MenuItem>
          {accountTypeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Branche" value={fIndustry} onChange={(e) => setFIndustry(e.target.value)} sx={{ minWidth: 160, bgcolor: 'background.paper' }}>
          <MenuItem value="">Alle</MenuItem>
          {industryOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </Stack>

      {/* Tabelle als Fläche */}
      <SectionCard sx={{ p: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          onRowClick={(params) => navigate(`/accounts/${params.row.id}`)}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={56}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ noRowsOverlay: NoRows, noResultsOverlay: NoResults }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
          }}
        />
      </SectionCard>

      {/* Dialog: Neues Unternehmen */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Neues Unternehmen erstellen</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" color="text.secondary">Stammdaten</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField name="name" label="Name" value={newAccount.name} onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })} fullWidth required />
              </Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Kontaktdaten</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField name="street" label="Straße" value={newAccount.street} onChange={(e) => setNewAccount({ ...newAccount, street: e.target.value })} fullWidth />
                <TextField name="zip_code" label="PLZ" value={newAccount.zip_code} onChange={(e) => setNewAccount({ ...newAccount, zip_code: e.target.value })} fullWidth />
                <TextField name="city" label="Stadt" value={newAccount.city} onChange={(e) => setNewAccount({ ...newAccount, city: e.target.value })} fullWidth />
                <TextField name="email" label="E-Mail" type="email" value={newAccount.email} onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })} fullWidth />
                <TextField name="phone" label="Telefon" value={newAccount.phone} onChange={(e) => setNewAccount({ ...newAccount, phone: e.target.value })} fullWidth />
                <TextField name="website" label="Webseite" value={newAccount.website ?? ''} onChange={(e) => setNewAccount({ ...newAccount, website: e.target.value })} fullWidth />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 3 } }}>
              <Typography variant="overline" color="text.secondary">Klassifizierung</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select name="status" label="Phase" value={newAccount.status ?? 'Open'} onChange={(e) => setNewAccount({ ...newAccount, status: e.target.value })} fullWidth>
                  {accountPhases.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                  {accountLost && <MenuItem value={accountLost.key}>{accountLost.label}</MenuItem>}
                </TextField>
                <TextField select name="account_type" label="Typ" value={newAccount.account_type ?? ''} onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })} fullWidth>
                  {accountTypeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select name="account_segment" label="Segment" value={newAccount.account_segment ?? ''} onChange={(e) => setNewAccount({ ...newAccount, account_segment: e.target.value })} fullWidth>
                  {accountSegmentOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>Unternehmensprofil</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField select name="organization_type" label="Org.-Typ" value={newAccount.organization_type ?? ''} onChange={(e) => setNewAccount({ ...newAccount, organization_type: e.target.value })} fullWidth>
                  {organizationTypeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select name="organization_size" label="Größe" value={newAccount.organization_size ?? ''} onChange={(e) => setNewAccount({ ...newAccount, organization_size: e.target.value })} fullWidth>
                  {organizationSizeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select name="revenue_range" label="Umsatz" value={newAccount.revenue_range ?? ''} onChange={(e) => setNewAccount({ ...newAccount, revenue_range: e.target.value })} fullWidth>
                  {revenueRangeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select name="industry" label="Branche" value={newAccount.industry ?? ''} onChange={(e) => setNewAccount({ ...newAccount, industry: e.target.value })} fullWidth>
                  {industryOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select name="legal_form" label="Rechtsform" value={newAccount.legal_form ?? ''} onChange={(e) => setNewAccount({ ...newAccount, legal_form: e.target.value })} fullWidth>
                  {legalFormOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button onClick={handleFormSubmit} variant="contained" disabled={!newAccount.name}>Erstellen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AccountList;
